import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';

import { Notification } from '@bringg/react-components';
import { UserType as UserTypeInterface } from '@bringg/types';
import { UserTypeStore } from '@bringg-frontend/global-stores';
import { useTranslation } from 'react-i18next';

import { useTranslations } from './use-translations';
import UserType from '../../stores/user-type/domain-objects/user-type';
interface ModalConfig {
	isOpen: boolean;
	isEditMode: boolean;
	translations: ReturnType<typeof useTranslations>;
	initialValues: Partial<UserTypeInterface>;
	onSubmit: (data: Record<string, unknown>) => Promise<void>;
	onClose: () => void;
}

interface UserTypeModalContextType {
	modalConfig: ModalConfig;
	openCreateModal: () => void;
	openEditModal: (userTypeId: number) => void;
}

const UserTypeModalContext = createContext<UserTypeModalContextType | undefined>(undefined);

export const useUserTypeModal = () => {
	const context = useContext(UserTypeModalContext);
	if (!context) {
		throw new Error('useUserTypeModal must be used within UserTypeModalProvider');
	}
	return context;
};

enum ModalMode {
	CREATE = 'create',
	EDIT = 'edit'
}

interface Props {
	children: ReactNode;
	userTypeStore: UserTypeStore;
}

export const UserTypeModalProvider = ({ children, userTypeStore }: Props) => {
	const { t } = useTranslation();
	const [mode, setMode] = useState<ModalMode | null>(null);
	const [selectedUserType, setSelectedUserType] = useState<Partial<UserTypeInterface> | null>(null);

	const isEditMode = mode === ModalMode.EDIT;
	const translations = useTranslations(isEditMode);

	const openCreateModal = useCallback(() => {
		setMode(ModalMode.CREATE);
		setSelectedUserType(null);
	}, []);

	const openEditModal = useCallback(
		(userTypeId: number) => {
			const userType = userTypeStore?.get(userTypeId);
			if (userType) {
				setSelectedUserType(userType.asJson);
				setMode(ModalMode.EDIT);
			}
		},
		[userTypeStore]
	);

	const closeModal = useCallback(() => {
		setSelectedUserType(null);
		setMode(null);
	}, []);

	const handleSubmit = useCallback(
		async (formData: Partial<UserTypeInterface>) => {
			try {
				if (isEditMode && selectedUserType) {
					const userType = userTypeStore?.get(selectedUserType.id);
					await userType.update(formData);

					Notification.success(
						t('USER_TYPE.USER_TYPE_UPDATED_SUCCESSFULLY', { userTypeTitle: formData.title })
					);
				} else {
					const newUserType = new UserType(formData, userTypeStore);
					await newUserType.create();
					Notification.success(
						t('USER_TYPE.USER_TYPE_CREATED_SUCCESSFULLY', { userTypeTitle: formData.title })
					);
				}
				closeModal();
			} catch (error) {
				Notification.error(translations.failedToSaveUserType);
				throw error;
			}
		},
		[isEditMode, selectedUserType, userTypeStore, translations, closeModal]
	);

	const modalConfig: ModalConfig = useMemo(() => {
		return {
			isOpen: Boolean(mode),
			isEditMode,
			translations,
			initialValues: selectedUserType,
			onSubmit: handleSubmit,
			onClose: closeModal
		};
	}, [mode, isEditMode, selectedUserType, translations, handleSubmit, closeModal]);

	return (
		<UserTypeModalContext.Provider
			value={{
				modalConfig,
				openCreateModal,
				openEditModal
			}}
		>
			{children}
		</UserTypeModalContext.Provider>
	);
};
